import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tabs from '../../Tabs/Tabs';
import SiteDetailPageTabsContent from './SiteDetailPageTabsContent';
import { EditTabs } from '../../Icons/Icons';
import Button from '../../Button/Button';
import HeaderIconTooltip from '../../HeaderIconTooltip/HeaderIconTooltip';
import NoteViewAddEditModal from '../../Notes/Modals/NoteViewAddEditModal';
import { useUrlModals } from '../../../utils/useUrlModals';
import { getContactHistory, getHighestPriorityFromNotes } from '../../../utils/api/site';

const MODAL_CONFIG = {
  noteView: { isIdParam: true },
};

const SiteDetailPageTabs = ({
  tabsData,
  onDragEnd = () => { },
  activeTab,
  setActiveTab,
  organizationId,
  siteId,
  siteName,
  isOpenAddEditNoteModal,
  setOpenAddNoteModal,
  showToast,
  setSiteData,
  currentUserId,
  currentUserRole,
}) => {
  const [editOrderTabs, setEditOrderTabs] = useState(false);

  // URL-based modal management for note view modal
  const { modals, openModal, closeModal } = useUrlModals(MODAL_CONFIG);

  // set the first tab as active tab if no active tab is set
  useEffect(() => {
    if (tabsData.length > 0 && !editOrderTabs && !activeTab) {
      setActiveTab(tabsData[0].title);
    }
  }, [tabsData, editOrderTabs, activeTab, setActiveTab]);

  const onTabClick = (e, data) => {
    e.preventDefault();
    setActiveTab(data);
  };

  const handleOrderEditing = () => {
    setEditOrderTabs(!editOrderTabs);
  };

  const tooltipEndMessage = (
    <>
      <div>dragging and dropping. The tabs will</div>
      <div>be reordered for everyone in your</div>
      <div>organization.</div>
    </>
  );

  const activeTabData = tabsData.find((tab) => tab.title === activeTab);

  const handleAddNote = (note) => {
    setSiteData((prev) => {
      const prevNotes = prev.notes || [];
      let updatedNotes;

      if (note.deleted) {
        updatedNotes = prevNotes.filter((n) => n.id !== note.id);
      } else {
        const noteExists = prevNotes.some((n) => n.id === note.id);
        updatedNotes = noteExists
          ? prevNotes.map((n) => (n.id === note.id ? note : n))
          : [...prevNotes, note];
      }

      return {
        ...prev,
        notes: updatedNotes,
        priority: getHighestPriorityFromNotes(updatedNotes),
      };
    });

    setActiveTab('Notes');
    // Close both modals
    setOpenAddNoteModal(false);
    closeModal('noteView');
  };

  const handleOpenNoteModal = (noteId) => {
    if (noteId) {
      openModal('noteView', noteId);
    }
  };

  const handleAddContact = (contact) => {
    setSiteData((prev) => ({ ...prev, contacts: [...prev.contacts, contact], primary_contact_id: prev.contacts.length === 0 ? contact.id : prev.primary_contact_id }));
    // update contact history after adding a contact
    getContactHistory(siteId, organizationId, 1, 10)
      .then((response) => response.json())
      .then((data) => {
        setSiteData((prev) => ({
          ...prev,
          contact_histories: {
            paginated_histories: data.histories,
            total_count: data.total_count,
            total_pages: data.total_pages,
            offset: data.current_page - 1,
            limit: data.current_count,
          },
        }));
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  };

  // Determine which modal to show and the note ID
  const showNoteModal = isOpenAddEditNoteModal || modals.noteView?.isOpen;
  const activeNoteId = modals.noteView?.isOpen ? modals.noteView.value : null;
  const isAddMode = isOpenAddEditNoteModal && !modals.noteView?.isOpen;

  const handleSetOpenAddEditModal = (isOpen) => {
    if (isOpen) {
      // This shouldn't happen in normal flow, but handle it just in case
      setOpenAddNoteModal(true);
    } else {
      // Close whichever modal is open
      if (isOpenAddEditNoteModal) {
        setOpenAddNoteModal(false);
      }
      if (modals.noteView?.isOpen) {
        closeModal('noteView');
      }
    }
  };

  if (!activeTabData) {
    return null;
  }

  return (
    <div className="w-full min-h-125">   {/* min-height to prevent extreme tabs 'jumping' */}
      <div className="h-27.75 flex flex-col">
        <div className="flex-grow" />
        <div className="flex h-11.5">
          <Tabs
            tabsData={tabsData}
            onClick={onTabClick}
            activeTab={activeTab}
            isDraggable={editOrderTabs}
            onDragEnd={onDragEnd}
          />
          <div className="flex-1" />
          <div
            role="button"
            onClick={handleOrderEditing}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleOrderEditing(e);
              }
            }}
          >
            {editOrderTabs ? <Button label="Save order" size="md" /> : (
              <HeaderIconTooltip
                divTextStart="You can edit the order of the tabs by"
                divTextEnd={tooltipEndMessage}
                place="bottom-end"
                icon={<EditTabs />}
                paddingBottom="0"
                className="p-0 text-left"
                margins="mr-3.75 mb-0 my-auto"
                border="border-0"

              />
            )}
          </div>
        </div>
      </div>
      <SiteDetailPageTabsContent
        data={activeTabData}
        handleAddContact={handleAddContact}
        handleOpenNoteModal={handleOpenNoteModal}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        handleChangeNote={handleAddNote}
      />
      {showNoteModal && (
        <NoteViewAddEditModal
          activeNoteId={activeNoteId}
          organizationId={organizationId}
          setOpenAddEditModal={handleSetOpenAddEditModal}
          siteId={siteId}
          currentUserId={currentUserId}
          siteName={siteName}
          isAddMode={isAddMode}
          handleChangeNote={handleAddNote}
          showToast={showToast}
        />
      )}
    </div>
  );
};

SiteDetailPageTabs.propTypes = {
  tabsData: PropTypes.array.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  siteName: PropTypes.string.isRequired,
  isOpenAddEditNoteModal: PropTypes.bool.isRequired,
  setOpenAddNoteModal: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  setSiteData: PropTypes.func.isRequired,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default SiteDetailPageTabs;
